import angular from "angular";

import JSZip from "@progress/jszip-esm";

import { saveAs } from "file-saver";

import { Ajax, Dates, Invoice, InvoiceType, PaymentChannel, Texts, Translations } from "@salaxy/core";

import { UiHelpers } from "../../services";

/** File type id */
enum FileTypeId {
  SepaFile = "sepaFile",
  SepaFileV03 = "sepaFileV03",
  SepaCopy = "sepaCopy",
  SepaCopyV03 = "sepaCopyV03",
  PdfFile = "pdfFile",
  FinvoiceFile = "finvoiceFile"
}

/**
 * Provides functionality to export invoices in various formats.
 */
export class InvoiceToolsController implements angular.IController {

  /**
   * For NG-dependency injection
   * @ignore
   */
  public static $inject = [
    "AjaxNg1",
    "$http",
    "UiHelpers"
  ];

  /** Export format */
  public fileTypeId: string = FileTypeId.PdfFile;

  /** Supported export formats */
  public fileTypes: {
    /** File type id */
    id: string,
    /** File type label */
    label: string,
    /** Workflow message after export */
    workflowMessage,
    /** Icon */
    icon: string,
  }[] = [
      {
        id: FileTypeId.PdfFile,
        label: Translations.get("SALAXY.ENUM.ExportMethod.filePdf.label"),
        workflowMessage: Translations.get("SALAXY.ENUM.ExportMethod.filePdf.label"),
        icon: "fa-file-pdf-o",
      },
      {
        id: FileTypeId.SepaFile,
        label: Translations.get("SALAXY.ENUM.ExportMethod.fileSepa.label"),
        workflowMessage: Translations.get("SALAXY.ENUM.ExportMethod.fileSepa.workflowMessage"),
        icon: "fa-file-code-o",
      },
      {
        id: FileTypeId.SepaFileV03,
        label: Translations.get("SALAXY.ENUM.ExportMethod.fileSepaV03.label"),
        workflowMessage: Translations.get("SALAXY.ENUM.ExportMethod.fileSepaV03.workflowMessage"),
        icon: "fa-file-code-o",
      },
      {
        id: FileTypeId.SepaCopy,
        label: Translations.get("SALAXY.ENUM.ExportMethod.copySepa.label"),
        workflowMessage: Translations.get("SALAXY.ENUM.ExportMethod.copySepa.workflowMessage"),
        icon: "fa-clipboard",
      },
      {
        id: FileTypeId.SepaCopyV03,
        label: Translations.get("SALAXY.ENUM.ExportMethod.copySepaV03.label"),
        workflowMessage: Translations.get("SALAXY.ENUM.ExportMethod.copySepaV03.workflowMessage"),
        icon: "fa-clipboard",
      },
      {
        id: FileTypeId.FinvoiceFile,
        label: Translations.get("SALAXY.ENUM.ExportMethod.finvoiceFile.label"),
        workflowMessage: Translations.get("SALAXY.ENUM.ExportMethod.finvoiceFile.workflowMessage"),
        icon: "fa-file-code-o",
      },
    ];


  /** Filetype Enum Options for filetype select element*/
  public get fileTypeOptions() {
    if (!this._fileTypeOptions) {
      this._fileTypeOptions = this.fileTypes.map((x) => ({ value: x.id, text: x.label }));
    }
    return this._fileTypeOptions;
  }
  /**
   * Object with read function to call for retrieving the invoice data.
   * Function has the following argument:  arr, which is an array of invoices into which the result will be appended.
   */
  public reader: {
    /** Function for reading invoices into given array. */
    read: (
      /** Array for invoices */
      arr: Invoice[],
      /** Message for workflow (loading) */
      message: string,
    ) => Promise<void>,
    /** Function for getting sepa data for invoices */
    getSepaData?: (
      /** Array for invoices */
      arr: Invoice[],
    ) => Promise<string>,
    /** Function for getting finvoice data for invoices */
    getFinvoiceData?: (
      /** Array for invoices */
      arr: Invoice[],
    ) => Promise<Blob[]>,
    /** Function for getting pdf data for invoices */
    getPdfData?: (
      /** Array for invoices */
      arr: Invoice[],
    ) => Promise<Blob[]>,
    /** Optional label for data export */
    exportLabel?: string,
    /** Indicates if the export is not possible */
    disabled: () => boolean,
  };

  private _fileTypeOptions = null;
  constructor(
    private ajax: Ajax,
    private $http: angular.IHttpService,
    private uiHelpers: UiHelpers
  ) {
  }

  /**
   * Initialize controller values.
   */
  public $onInit() {
    // Nothing here
  }

  /** Set visible filetypes. Sets the first file type as default. */
  public setFileTypes(fileTypes: string[]) {
    if (fileTypes && fileTypes.length > 0) {
      const fts = [];
      fileTypes.forEach((x) => {
        const ft = this.fileTypes.find((f) => f.id === x);
        if (ft) {
          fts.push(ft);
        }
      });
      this.fileTypes.splice(0);
      this.fileTypes.push(...fts);
      this.fileTypeId = fileTypes[0];
    }
  }

  /** Exports data in defined format. */
  public export(
    reader: {
      /** Function for reading data into given array. */
      read: (
        /** Array for invoices */
        arr: Invoice[],
        /** Workflow loading message */
        message: string,
      ) => Promise<void>,
      /** Function for getting sepa data for invoices */
      getSepaData?: (
        /** Array for invoices */
        arr: Invoice[],
      ) => Promise<string>,
      /** Function for getting finvoice data for invoices */
      getFinvoiceData?: (
        /** Array for invoices */
        arr: Invoice[],
      ) => Promise<Blob[]>,
      /** Function for getting pdf data for invoices */
      getPdfData?: (
        /** Array for invoices */
        arr: Invoice[],
      ) => Promise<Blob[]>,
      /** Optional label for data export */
      exportLabel?: string,
      /** Indicates if the export is not possible */
      disabled: () => boolean,
    } = null,
    /** If true, does not show loading indicator. Default is false. */
    hideLoading = false,
    /** If true, does not return any content. */
    withoutContent = false,
    /** Prefer zip. If true creates zips for one content file too. */
    preferZip = false,
  ): Promise<void> {
    if (!reader) {
      reader = this.reader;
    }
    if (!reader) {
      console.error("No reader for invoices.");
      return Promise.resolve();
    }
    const loading = hideLoading ? { dismiss: () => null } : this.uiHelpers.showLoading("SALAXY.UI_Terms.wait");

    const arr: Invoice[] = [];
    return reader.read(arr, this.getWorkflowMessage()).then(() => {
      if (arr.length === 0) {
        loading.dismiss();
        return Promise.resolve();
      }
      if (withoutContent) {
        return;
      }
      switch (this.fileTypeId) {
        case FileTypeId.PdfFile:
          return (reader.getPdfData ?? ((x) => this.getPdfData(x)))(arr).then((pdfArr) => {
            if (pdfArr.length === 1 && !preferZip) {
              saveAs(pdfArr[0], `${this.getFileName(arr[0])}.pdf`);
              loading.dismiss();
              return;
            } else {
              let counter = 0;
              const zip = new JSZip();
              const next = () => {
                if (counter === pdfArr.length) {
                  return;
                }
                zip.file(`${this.getFileName(arr[counter])}.pdf`, pdfArr[counter]);
                counter++;
                next();
              };

              next();
              return zip.generateAsync({
                type: "blob",
                mimeType:
                  "application/zip",
              }).then((blob) => {
                saveAs(blob, `${this.getZipFileName(arr)}.zip`);
                loading.dismiss();
                return;
              });
            }
          });
          break;
        case FileTypeId.SepaFile:
        case FileTypeId.SepaFileV03:
          return (reader.getSepaData ?? ((x) => this.getSepaData(x)))(arr).then((sepaData) => {
            saveAs(new Blob([sepaData], { type: "text/xml;charset=utf-8" }), arr.length === 1 ? `${this.getFileName(arr[0])}.xml` : `${this.getZipFileName(arr)}.xml`);
            loading.dismiss();
            return;
          });
          break;
        case FileTypeId.SepaCopy:
        case FileTypeId.SepaCopyV03:
          return (reader.getSepaData ?? ((x) => this.getSepaData(x)))(arr).then((sepaData) => {
            (navigator as any).clipboard.writeText(sepaData);
            loading.dismiss();
            return;
          });
        case FileTypeId.FinvoiceFile:
          return (reader.getFinvoiceData ?? ((x) => this.getFinvoiceData(x)))(arr).then((finvoiceArr) => {
            if (finvoiceArr.length === 1 && !preferZip) {
              saveAs(finvoiceArr[0], `${this.getFileName(arr[0])}.xml`);
              loading.dismiss();
              return;
            } else {
              let counter = 0;
              const zip = new JSZip();
              const next = () => {
                if (counter === finvoiceArr.length) {
                  return;
                }
                zip.file(`${this.getFileName(arr[counter])}.xml`, finvoiceArr[counter]);
                counter++;
                next();
              };

              next();
              return zip.generateAsync({
                type: "blob",
                mimeType:
                  "application/zip",
              }).then((blob) => {
                saveAs(blob, `${this.getZipFileName(arr)}.zip`);
                loading.dismiss();
                return;
              });
            }
          });
          break;
      }
    });
  }

  /**
   * Create single invoice.
   * @param channel Payment channel.
   * @param invoiceType Invoice type.
   * @returns New/updated invoice.
   */
  public createSingleInvoice(channel: PaymentChannel, invoiceType: InvoiceType): Promise<Invoice> {
    const loading = this.uiHelpers.showLoading();
    return this.ajax.postJSON(`/v03/api/invoices/create/${channel}/${invoiceType}?month=${Dates.format(new Date(),"YYYY-MM-01")}`, null).then((invoice) => {
      loading.dismiss();
      return this.uiHelpers.showAlert("Lasku luotu","Päivitä lista.").then(() => invoice);
    });
  }

  /**
   * Returns the file type definition
   * @param fileTypeId File type id.
   */
  public getFileType(fileTypeId: string) {
    return this.fileTypes.find((x) => x.id === fileTypeId);
  }


  private getFileName(invoice: Invoice) {
    return Texts.escapeFileName(`${Translations.get("SALAXY.UI_Terms.downLoadableMaterialTypes.payment")}_${invoice.payer.avatar.displayName}_${invoice.id}`);
  }

  private getZipFileName(invoices: Invoice[]) {
    return Texts.escapeFileName(`${Translations.get("SALAXY.UI_Terms.downLoadableMaterialTypes.payments")}${invoices[0].payer.avatar.displayName}_${Dates.format("today","YYYYMMDD")}`);
  }

  private getWorkflowMessage() {
    const fileType = this.getFileType(this.fileTypeId);
    if (fileType) {
      return fileType.workflowMessage;
    }
    return null;
  }

  private getPdfData(invoices: Invoice[]): Promise<Blob[]> {
    const promises = invoices.map(x => this.download("/v03/api/invoices/content", x, "blob"));
    return Promise.all(promises);
  }

  private getFinvoiceData(invoices: Invoice[]): Promise<Blob[]> {
    const promises = invoices.map(x => this.download("/v03/api/invoices/finvoice", x, "blob"));
    return Promise.all(promises);
  }

  private getSepaData(invoices: Invoice[], payerIbanNumber: string = null): Promise<string> {
    const version = this.fileTypeId === FileTypeId.SepaFileV03 || this.fileTypeId === FileTypeId.SepaCopyV03 ? "pain.001.001.03" : "pain.001.001.02";
    const method = `/v03/api/invoices/sepa?version=${version}${payerIbanNumber ? "&payerIbanNumber=" + encodeURIComponent(payerIbanNumber) : ""}`;
    return this.download(method, invoices, "text");
  }
  private download(method: string, data: any, responseType: string): Promise<any> {

    const request: any = {}; // angular.IRequestConfig

    const token: string = this.ajax.getCurrentToken();
    if (token) {
      request.headers = { Authorization: "Bearer " + token };
    }

    request.url = this.ajax.getServerAddress() + method;
    request.method = "POST";
    request.data = data;
    request.responseType = responseType;
    request.withCredentials = (token) ? false : this.ajax.useCredentials;

    return (this.$http(request).then(
      (response) => response.data,
      (error: any) => {
        return null;
      }) as any
    );
  }
}
